// Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { FunctionComponent, ReactElement, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { DefaultLayout } from "../layouts/DefaultLayout";
import { Hooks, useAuthContext } from "@asgardeo/auth-react";
import { LogoutRequestDenied } from "../components/LogoutRequestDenied";

/**
 * Decoded ID Token Response component Prop types interface.
 */
type LoggedOutPagePropsInterface = {};

/**
 * Page to display for 404.
 *
 * @param {LoggedOutPagePropsInterface} props - Props injected to the component.
 *
 * @return {React.ReactElement}
 */
export const LoggedOutPage: FunctionComponent<
    LoggedOutPagePropsInterface> = (): ReactElement => {
    const USER_DENIED_LOGOUT = "End User denied the logout request";
    const { signIn, signOut, on } = useAuthContext();
    const [hasLogoutFailureError, setHasLogoutFailureError] =
        useState<boolean>();

    const search = useLocation().search;
    const stateParam = new URLSearchParams(search).get("state");
    const errorDescParam = new URLSearchParams(search).get("error_description");
    const navigate = useNavigate();

    useEffect(() => {
        if (stateParam && errorDescParam) {
            if (errorDescParam === "End User denied the logout request") {
                setHasLogoutFailureError(true);
            }
        }
    }, [stateParam, errorDescParam]);

    const handleLogin = () => {
        setHasLogoutFailureError(false);
        signIn();
    };

    const handleLogout = () => {
        signOut();
    };

    /**
     * handles the error occurs when the logout consent page is enabled
     * and the user clicks 'NO' at the logout consent page
     */
    useEffect(() => {
        on(Hooks.SignOut, () => {
            setHasLogoutFailureError(false);
        });

        on(Hooks.SignOutFailed, () => {
            if (!errorDescParam) {
                handleLogin();
            }
        });
    }, [errorDescParam, handleLogin, on]);

    if (hasLogoutFailureError) {
        return (
            <LogoutRequestDenied
                errorMessage={USER_DENIED_LOGOUT}
                handleLogin={handleLogin}
                handleLogout={handleLogout}
            />
        );
    }

    return (
        <DefaultLayout>
            <h3>User Logged out successfully</h3>
            <button
                className="btn primary"
                style={{backgroundColor:"orange"}}
                onClick={() => {
                    navigate("/login");
                }}
            >
                Go back to Login
            </button>
        </DefaultLayout>
    );
};
